import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/admin/DashboardLayout';
import { Save, Loader2, Layout, Image as ImageIcon, X,
  Briefcase, Scissors, Shirt, Ruler, Factory, Truck, Package, ClipboardList, Tag, ShoppingBag, Users, PenTool, Monitor, Star, Layers, Palette, ShieldCheck, Zap, Clock
} from 'lucide-react';
import { useNotification } from '../../context/NotificationContext';
import MediaManager from '../../components/admin/MediaManager';
import DeleteConfirmationModal from '../../components/admin/DeleteConfirmationModal';

// --- ICON OPTIONS (Reused & Expanded for Convection) ---
const iconOptions = [
  { name: 'scissors', icon: <Scissors size={24} />, label: 'Cutting / Pola' },
  { name: 'pentool', icon: <PenTool size={24} />, label: 'Desain / Pola' },
  { name: 'shirt', icon: <Shirt size={24} />, label: 'Jahit / Produk' },
  { name: 'briefcase', icon: <Briefcase size={24} />, label: 'Finishing / Bisnis' },
  { name: 'award', icon: <ShieldCheck size={24} />, label: 'Quality Control / Award' }, // Mapped 'award' to ShieldCheck visually if preferred, or use Award icon if available. Let's import Award too.
  { name: 'layers', icon: <Layers size={24} />, label: 'Material / Bahan' },
  { name: 'clock', icon: <Clock size={24} />, label: 'Waktu / Deadline' },
  { name: 'truck', icon: <Truck size={24} />, label: 'Pengiriman' },
  { name: 'factory', icon: <Factory size={24} />, label: 'Produksi Massal' },
  { name: 'package', icon: <Package size={24} />, label: 'Packing' },
  { name: 'ruler', icon: <Ruler size={24} />, label: 'Pengukuran' },
  { name: 'palette', icon: <Palette size={24} />, label: 'Warna / Sablon' },
  { name: 'zap', icon: <Zap size={24} />, label: 'Cepat / Express' },
  { name: 'users', icon: <Users size={24} />, label: 'Team / SDM' },
  { name: 'tag', icon: <Tag size={24} />, label: 'Labeling' },
];

const StaticContentManager = () => {
  const { addNotification } = useNotification();
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  // Media Manager State
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [activeMediaField, setActiveMediaField] = useState(null); // { section, index, isArray }

  // Icon Picker State
  const [showIconModal, setShowIconModal] = useState(false);
  const [activeIconField, setActiveIconField] = useState(null); // { section, index }

  // Delete Modal State for Partners
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState(null); // { pIndex, section, index, partnersArray }

  // Load Data
  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/static_content.php`);
      setContent(res.data);
      if (activeTab === 'all' && Object.keys(res.data).length > 0) {
        setActiveTab(Object.keys(res.data)[0]);
      }
    } catch (err) {
      console.error(err);
      addNotification('Gagal memuat konten.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle Text Input Change
  const handleChange = (section, index, value) => {
    const newContent = { ...content };
    newContent[section][index].content_value_id = value;
    setContent(newContent);
  };

  // Handle Media Selection
  const handleMediaSelect = (url) => {
    if (!activeMediaField) return;

    const { section, index, isArray, subIndex } = activeMediaField;
    const item = content[section][index];
    
    if (isArray === 'partner_item') {
        // Handle Partner Object Array
        let partners = [];
        try {
            partners = JSON.parse(item.content_value_id || '[]');
        } catch (e) { partners = []; }
        
        if (subIndex !== undefined && partners[subIndex]) {
            partners[subIndex].image = url;
            handleChange(section, index, JSON.stringify(partners));
            addNotification('Logo partner diperbarui!', 'success');
        }
    } else if (isArray) {
        // Handle Array (Carousel)
        let currentImages = [];
        try {
            currentImages = JSON.parse(item.content_value_id || '[]');
            if (!Array.isArray(currentImages)) currentImages = [item.content_value_id];
        } catch (e) {
            currentImages = item.content_value_id ? [item.content_value_id] : [];
        }
        currentImages.push(url);
        handleChange(section, index, JSON.stringify(currentImages));
        addNotification('Gambar berhasil ditambahkan!', 'success');
    } else {
        // Handle Single Image
        handleChange(section, index, url);
        addNotification('Gambar berhasil dipilih!', 'success');
    }
  };

  // Open Media Manager
  const openMediaManager = (section, index, isArray = false, subIndex = null) => {
    setActiveMediaField({ section, index, isArray, subIndex });
    setShowMediaModal(true);
  };

  // Open Icon Picker
  const openIconPicker = (section, index) => {
    setActiveIconField({ section, index });
    setShowIconModal(true);
  }

  const handleIconSelect = (iconName) => {
    if (activeIconField) {
        handleChange(activeIconField.section, activeIconField.index, iconName);
        setShowIconModal(false);
        setActiveIconField(null);
    }
  }

  // Save Changes
  const handleSave = async () => {
    setSaving(true);
    
    const updates = [];
    Object.keys(content).forEach(section => {
      content[section].forEach(item => {
        updates.push({
          id: item.id,
          content_value_id: item.content_value_id
        });
      });
    });

    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/static_content.php`, { updates });
      addNotification('Data statistik berhasil diperbarui!', 'success');
    } catch (err) {
      addNotification('Gagal menyimpan data.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Trigger Delete Partner Modal
  const initiateRemovePartner = (pIndex, section, index, partners) => {
    setPartnerToDelete({ pIndex, section, index, partners });
    setShowDeleteModal(true);
  };

  // Confirm Delete Partner
  const confirmDeletePartner = () => {
    if (!partnerToDelete) return;
    const { pIndex, section, index, partners } = partnerToDelete;
    
    const newPartners = partners.filter((_, i) => i !== pIndex);
    handleChange(section, index, JSON.stringify(newPartners));
    
    addNotification('Partner dihapus.', 'success');
    setShowDeleteModal(false);
    setPartnerToDelete(null);
  };

  const sectionConfig = [
    { key: 'hero_editorial', label: 'Hero Editorial' },
    { key: 'material_lab', label: 'Material Lab' },
    { key: 'production_process', label: 'Production Process' },
    { key: 'archive_portfolio', label: 'Archive / Highlight' },
    { key: 'partnership', label: 'Kemitraan / Partnership' },
    { key: 'faq', label: 'FAQ' },
    { key: 'about', label: 'Tentang Kami' },
    { key: 'contact', label: 'Kontak' }
  ];

  // Filter sections that actually exist in the content (or just show all defined)
  const sections = sectionConfig.filter(s => content[s.key] || s.key === activeTab).map(s => s.key);

  const styles = {
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
    title: { fontSize: '1.5rem', fontWeight: '700' },
    saveBtn: { 
      backgroundColor: '#111', color: '#fff', padding: '0.75rem 1.5rem', 
      borderRadius: '0.5rem', border: 'none', cursor: 'pointer', 
      display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600' 
    },
    tabs: { display: 'flex', gap: '1rem', marginBottom: '2rem', overflowX: 'auto', paddingBottom: '0.5rem' },
    tab: (isActive) => ({
      padding: '0.5rem 1rem', borderRadius: '0.5rem', cursor: 'pointer', 
      backgroundColor: isActive ? '#eff6ff' : 'transparent', 
      color: isActive ? '#2563eb' : '#6b7280', fontWeight: isActive ? '600' : '500',
      border: isActive ? '1px solid #bfdbfe' : '1px solid transparent',
      whiteSpace: 'nowrap'
    }),
    card: { backgroundColor: '#fff', padding: '1.5rem', borderRadius: '1rem', border: '1px solid #e5e7eb', marginBottom: '1.5rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
    cardTitle: { fontSize: '1.1rem', fontWeight: '700', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'capitalize' },
    fieldGroup: { marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #f3f4f6' },
    label: { fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.5rem', color: '#374151', display: 'block' },
    input: { width: '100%', padding: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', fontSize: '0.9rem', fontFamily: 'inherit', maxWidth: '400px' },
    imagePreview: { width: '100%', maxWidth: '300px', height: '150px', objectFit: 'cover', borderRadius: '0.5rem', marginBottom: '0.5rem', backgroundColor: '#f9f9f9', border: '1px dashed #ccc' },
    selectBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500', transition: 'background-color 0.2s' },
    groupCard: { backgroundColor: '#f8fafc', padding: '1.5rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0', marginBottom: '1.5rem' },
    groupTitle: { fontSize: '1rem', fontWeight: '700', marginBottom: '1rem', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
    
    // Icon Picker Modal Styles
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, backdropFilter: 'blur(5px)' },
    modal: { backgroundColor: '#fff', padding: '2rem', width: '90%', maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto', borderRadius: '1rem', border: '1px solid #e5e5e5', boxShadow: '0 20px 50px rgba(0,0,0,0.1)' },
    iconGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '1rem', marginTop: '1rem' },
    iconItem: (selected) => ({
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      border: selected ? '2px solid #000' : '1px solid #eee', borderRadius: '0.5rem', cursor: 'pointer',
      backgroundColor: selected ? '#f0f9ff' : '#fff', transition: 'all 0.2s', gap: '0.5rem'
    })
  };

  const renderField = (item, index) => (
    <div key={item.id} style={styles.fieldGroup}>
      <label style={styles.label}>{item.description || item.content_key}</label>
      
      {/* --- CUSTOM HANDLER FOR PARTNERS (Advanced) --- */}
      {item.content_key === 'home_partners' ? (
        <div style={{backgroundColor: '#f8fafc', padding: '1.5rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0'}}>
            <h3 style={{fontSize: '1rem', fontWeight: '700', marginBottom: '1rem', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem'}}>
                Management Partner & Klien
            </h3>
            {(() => {
                let partners = [];
                try {
                    partners = JSON.parse(item.content_value_id || '[]');
                    // Migration: Convert old string array to objects if needed
                    if (Array.isArray(partners) && partners.length > 0 && typeof partners[0] === 'string') {
                        partners = partners.map(url => ({ image: url, description: '', link: '' }));
                    }
                } catch (e) { partners = []; }

                const updatePartner = (pIndex, field, val) => {
                    const newPartners = [...partners];
                    newPartners[pIndex][field] = val;
                    handleChange(activeTab, index, JSON.stringify(newPartners));
                };

                const addPartner = () => {
                    const newPartners = [...partners, { image: '', description: '', link: '' }];
                    handleChange(activeTab, index, JSON.stringify(newPartners));
                };

                return (
                    <div style={{display:'flex', flexDirection:'column', gap:'1rem'}}>
                        {partners.map((p, pIndex) => (
                            <div key={pIndex} style={{
                                padding:'1rem', border:'1px solid #e5e7eb', borderRadius:'0.5rem', 
                                backgroundColor:'#f9fafb', display:'flex', gap:'1rem', alignItems:'flex-start'
                            }}>
                                {/* Image Column */}
                                <div style={{width:'120px', flexShrink:0}}>
                                    <div style={{
                                        width:'100%', height:'80px', backgroundColor:'#fff', border:'1px dashed #ccc', 
                                        borderRadius:'0.5rem', overflow:'hidden', marginBottom:'0.5rem', display:'flex', alignItems:'center', justifyContent:'center'
                                    }}>
                                        {p.image ? (
                                            <img src={p.image} alt="Partner" style={{maxWidth:'100%', maxHeight:'100%'}} />
                                        ) : <span style={{fontSize:'0.7rem', color:'#999'}}>No Image</span>}
                                    </div>
                                    <button 
                                        onClick={() => openMediaManager(activeTab, index, 'partner_item', pIndex)}
                                        style={{...styles.selectBtn, width:'100%', justifyContent:'center', fontSize:'0.7rem', padding:'0.3rem'}}
                                    >
                                        Upload
                                    </button>
                                </div>

                                {/* Text Column */}
                                <div style={{flex:1, display:'flex', flexDirection:'column', gap:'0.5rem'}}>
                                    <div style={{display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                                        <label style={{fontSize:'0.75rem', fontWeight:'600', color:'#4b5563'}}>Deskripsi / Nama Client:</label>
                                        <textarea 
                                            placeholder="Contoh: PT. Sumber Makmur (Seragam Kerja)" 
                                            value={p.description || ''}
                                            onChange={(e) => updatePartner(pIndex, 'description', e.target.value)}
                                            style={{...styles.input, width:'100%', minHeight:'60px', maxWidth:'100%'}}
                                        />
                                    </div>
                                    <div style={{display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                                        <label style={{fontSize:'0.75rem', fontWeight:'600', color:'#4b5563'}}>Link / Website (Opsional):</label>
                                        <input 
                                            type="text" 
                                            placeholder="https://..." 
                                            value={p.link || ''}
                                            onChange={(e) => updatePartner(pIndex, 'link', e.target.value)}
                                            style={{...styles.input, width:'100%', maxWidth:'100%'}}
                                        />
                                    </div>
                                </div>

                                {/* Action Column */}
                                <button 
                                    onClick={() => initiateRemovePartner(pIndex, activeTab, index, partners)}
                                    style={{
                                        color:'red', background:'none', border:'none', cursor:'pointer', padding:'0.5rem'
                                    }}
                                >
                                    X
                                </button>
                            </div>
                        ))}
                        <button onClick={addPartner} style={{
                            ...styles.selectBtn, alignSelf:'flex-start', backgroundColor:'#111', color:'#fff', border:'none'
                        }}>
                            + Tambah Partner
                        </button>
                    </div>
                );
            })()}
        </div>
      ) : 

      /* CUSTOM HANDLER FOR PARTNERSHIP POINTS */
      (item.content_key === 'partnership_brand_points' || item.content_key === 'partnership_investor_points') ? (
        <div style={{backgroundColor: '#f8fafc', padding: '1.5rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0'}}>
             <h3 style={{fontSize: '1rem', fontWeight: '700', marginBottom: '1rem', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem'}}>
                {item.description}
            </h3>
            {(() => {
                let points = [];
                try {
                    points = JSON.parse(item.content_value_id || '[]');
                } catch (e) { points = []; }

                const updatePoint = (pIndex, field, val) => {
                    const newPoints = [...points];
                    if (!newPoints[pIndex]) newPoints[pIndex] = {};
                    newPoints[pIndex][field] = val;
                    handleChange(activeTab, index, JSON.stringify(newPoints));
                };

                const addPoint = () => {
                    const newPoints = [...points, { title: '', desc: '' }];
                    handleChange(activeTab, index, JSON.stringify(newPoints));
                };

                 const removePoint = (pIndex) => {
                    const newPoints = points.filter((_, i) => i !== pIndex);
                    handleChange(activeTab, index, JSON.stringify(newPoints));
                };

                return (
                    <div style={{display:'flex', flexDirection:'column', gap:'1rem'}}>
                        {points.map((p, pIndex) => (
                            <div key={pIndex} style={{
                                padding:'1rem', border:'1px solid #e5e7eb', borderRadius:'0.5rem', 
                                backgroundColor:'#f9fafb', display:'flex', gap:'1rem', alignItems:'flex-start'
                            }}>
                                <div style={{flex:1, display:'flex', flexDirection:'column', gap:'0.5rem'}}>
                                    <input 
                                        type="text"
                                        placeholder="Judul Poin (Ex: Kualitas Konsisten)"
                                        value={p.title || ''}
                                        onChange={(e) => updatePoint(pIndex, 'title', e.target.value)}
                                        style={{...styles.input, width:'100%', fontWeight:'bold', maxWidth:'100%'}}
                                    />
                                    <textarea 
                                        placeholder="Deskripsi Poin..."
                                        value={p.desc || ''}
                                        onChange={(e) => updatePoint(pIndex, 'desc', e.target.value)}
                                        style={{...styles.input, width:'100%', minHeight:'60px', maxWidth:'100%'}}
                                    />
                                </div>
                                <button 
                                    onClick={() => removePoint(pIndex)}
                                    style={{color:'red', background:'none', border:'none', cursor:'pointer', padding:'0.5rem'}}
                                >
                                    X
                                </button>
                            </div>
                        ))}
                         <button onClick={addPoint} style={{
                            ...styles.selectBtn, alignSelf:'flex-start', backgroundColor:'#111', color:'#fff', border:'none'
                        }}>
                            + Tambah Poin
                        </button>
                    </div>
                );
            })()}
        </div>
      ) :
      
      /* CHECK FOR HERO IMAGE OR GALLERY (MULTI-IMAGE) - Removed 'partners_gallery' check implicitly by only checking key match */
      item.content_key === 'home_hero_image' || item.content_key.includes('_gallery') ? (
        <div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            {(() => {
              let images = [];
              try {
                images = JSON.parse(item.content_value_id || '[]');
                if (!Array.isArray(images)) images = [item.content_value_id];
              } catch (e) {
                images = item.content_value_id ? [item.content_value_id] : [];
              }
              
              return images.map((img, imgIndex) => (
                <div key={imgIndex} style={{ position: 'relative', width: '100px', height: '100px' }}>
                  {img.match(/\.(mp4|webm)$/i) ? (
                    <video src={img} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '0.5rem', border: '1px solid #ddd', background: '#000' }} />
                  ) : (
                    <img 
                      src={img} 
                      alt={`Slide ${imgIndex + 1}`} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '0.5rem', border: '1px solid #ddd' }} 
                    />
                  )}
                  <button
                    onClick={() => {
                      const newImages = images.filter((_, i) => i !== imgIndex);
                      handleChange(activeTab, index, JSON.stringify(newImages));
                    }}
                    style={{
                      position: 'absolute', top: -5, right: -5, background: 'red', color: 'white',
                      borderRadius: '50%', border: 'none', width: '20px', height: '20px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', zIndex: 10
                    }}
                  >
                    X
                  </button>
                </div>
              ));
            })()}
            
            {/* ADD BUTTON (Max 20) */}
            {(() => {
              let count = 0;
              try {
                 const parsed = JSON.parse(item.content_value_id || '[]');
                 count = Array.isArray(parsed) ? parsed.length : (item.content_value_id ? 1 : 0);
              } catch(e) { count = item.content_value_id ? 1 : 0; }

              if (count < 20) {
                return (
                  <button 
                    onClick={() => openMediaManager(activeTab, index, true)}
                    style={{ 
                      width: '100px', height: '100px', border: '2px dashed #ccc', borderRadius: '0.5rem',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      flexDirection: 'column', color: '#666', backgroundColor: '#f9f9f9', outline: 'none'
                    }}
                  >
                    <ImageIcon size={24} />
                    <span style={{fontSize: '0.7rem', marginTop: '4px'}}>+ Add Media</span>
                  </button>
                );
              }
            })()}
          </div>
          <p style={{fontSize: '0.8rem', color: '#666'}}>Maksimal 20 item (Gambar/Video).</p>
        </div>
      ) : 
      /* ICON PICKER (For keys like service_1_icon) */
      (item.content_key.includes('_icon')) ? (
        <div>
            <button 
                onClick={() => openIconPicker(activeTab, index)}
                style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem',
                    backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '0.5rem',
                    cursor: 'pointer', minWidth: '200px', transition: 'all 0.2s'
                }}
            >
                {/* Find current icon from options */}
                <div style={{
                    width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: '#f3f4f6', borderRadius: '0.25rem'
                }}>
                    {iconOptions.find(opt => opt.name === item.content_value_id)?.icon || <Layout size={20} />}
                </div>
                <div style={{display:'flex', flexDirection:'column', alignItems:'flex-start'}}>
                    <span style={{fontWeight:'600', fontSize:'0.9rem'}}>
                        {iconOptions.find(opt => opt.name === item.content_value_id)?.label || 'Pilih Icon...'}
                    </span>
                    <span style={{fontSize:'0.75rem', color:'#6b7280'}}>
                        ID: {item.content_value_id || 'none'}
                    </span>
                </div>
            </button>
        </div>
      ) :
      /* REGULAR IMAGE OR TEXT INPUT */
      (item.content_key.includes('_image') || item.content_type === 'image' || item.content_key === 'home_featured_image') ? (
        <div>
          {item.content_value_id && (
             item.content_value_id.match(/\.(mp4|webm)$/i) ? (
                <video 
                  src={item.content_value_id} 
                  style={{...styles.imagePreview, backgroundColor: '#000'}} 
                  controls
                />
             ) : (
                <img 
                  src={item.content_value_id} 
                  alt="Preview" 
                  style={styles.imagePreview} 
                  onError={(e) => {e.target.style.display='none'}}
                />
             )
          )}
          
          <button 
            onClick={() => openMediaManager(activeTab, index)}
            style={styles.selectBtn}
          >
            <ImageIcon size={16} />
            {item.content_value_id ? 'Ganti Gambar' : 'Pilih Gambar'}
          </button>

          <input 
            type="text" 
            value={item.content_value_id} 
            readOnly 
            style={{...styles.input, marginTop: '0.5rem', backgroundColor: '#f9f9f9', color: '#666', display: 'block'}} 
          />
        </div>
      ) : item.content_type === 'textarea' ? (
        <textarea
          style={{...styles.input, minHeight: '100px', maxWidth: '100%', resize: 'vertical'}}
          value={item.content_value_id}
          onChange={(e) => handleChange(activeTab, index, e.target.value)}
          placeholder="Masukkan teks..."
        />
      ) : (
        <input 
          type={item.content_type === 'number' ? 'number' : 'text'}
          style={styles.input}
          value={item.content_value_id}
          onChange={(e) => handleChange(activeTab, index, e.target.value)}
          placeholder="Masukkan data..."
        />
      )}
    </div>
  );

  const getRenderItems = (items) => {
    const renderList = [];
    const processedIndices = new Set();
    
    items.forEach((item, originalIndex) => {
        if (processedIndices.has(originalIndex)) return;

        const groupMatch = item.content_key.match(/^((?:cap|service)_\d+)_/);
        if (groupMatch) {
            const groupKey = groupMatch[1];
            // Find all related items along with their original indices
            const groupMembers = [];
            items.forEach((subItem, subIndex) => {
                 if (subItem.content_key.startsWith(groupKey + '_')) {
                     groupMembers.push({ ...subItem, originalIndex: subIndex });
                     processedIndices.add(subIndex);
                 }
            });
            renderList.push({ type: 'group', key: groupKey, items: groupMembers });
        } else {
            renderList.push({ type: 'single', data: item, originalIndex: originalIndex });
            processedIndices.add(originalIndex);
        }
    });
    return renderList;
  };

  return (
    <DashboardLayout>
      <MediaManager 
        isOpen={showMediaModal} 
        onClose={() => setShowMediaModal(false)} 
        onSelect={handleMediaSelect}
      />

      {/* Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeletePartner}
        isConfirming={false} // Sync delete, no loading state needed
        targetName="partner ini"
      />
      
      {/* Icon Picker Modal */}
      {showIconModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem'}}>
              <h3 style={{fontSize:'1.2rem', fontWeight:'700'}}>Pilih Icon</h3>
              <button onClick={() => setShowIconModal(false)} style={{border:'none', background:'none', cursor:'pointer'}}><X /></button>
            </div>
            <div style={styles.iconGrid}>
                {iconOptions.map(opt => (
                    <div 
                        key={opt.name} 
                        onClick={() => handleIconSelect(opt.name)}
                        style={styles.iconItem(activeIconField ? content[activeIconField.section][activeIconField.index].content_value_id === opt.name : false)}
                    >
                        {opt.icon}
                        <span style={{fontSize:'0.75rem', fontWeight:'500', color:'#444', textAlign:'center'}}>{opt.label}</span>
                    </div>
                ))}
            </div>
          </div>
        </div>
      )}

      <div style={styles.header}>
        <h1 style={styles.title}>Manajer Statistik</h1>
        <button style={{...styles.saveBtn, opacity: saving ? 0.7 : 1}} onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
          {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
        </button>
      </div>

      {loading ? <div>Memuat data...</div> : (
        <>
          {/* Tabs */}
          {sections.length > 1 && (
            <div style={styles.tabs}>
              {sectionConfig.map(sec => (
                <button 
                  key={sec.key} 
                  style={styles.tab(activeTab === sec.key)}
                  onClick={() => setActiveTab(sec.key)}
                >
                  {sec.label}
                </button>
              ))}
            </div>
          )}

          {/* Content Form */}
          {content[activeTab] && (
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>
                <Layout size={20} />
                Data: {sectionConfig.find(s => s.key === activeTab)?.label || activeTab}
              </h2>
              
              {getRenderItems(content[activeTab]).map((renderItem, i) => {
                if (renderItem.type === 'group') {
                    const [prefix, num] = renderItem.key.split('_');
                    const titlePrefix = prefix === 'cap' ? 'Capability' : 'Service';
                    return (
                        <div key={renderItem.key} style={styles.groupCard}>
                            <h3 style={styles.groupTitle}>
                                {titlePrefix} {num}
                            </h3>
                            {renderItem.items.map(gItem => renderField(gItem, gItem.originalIndex))}
                        </div>
                    );
                } else {
                    return renderField(renderItem.data, renderItem.originalIndex);
                }
              })}
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default StaticContentManager;
